/**
 * EnabledModulesManager.ts
 * Utility to retrieve and manage enabled compliance modules for the current company
 */

import { db } from '../../../external-js/config/main_config';
import { waitForCompanyID } from './waitForCompanyID.util';

/**
 * Extend the Window interface to include our custom properties
 */
declare global {
    interface Window { _SSR_FLAGS?: SSRFlags; }
}

/**
 * Type definition for SSR flags
 */
interface SSRFlags {
    complianceModules?: ComplianceModules;
    OrganizationID?: string;
}

/**
 * Types for module settings and enabled status
 */
export interface ModuleValue {
    value: boolean | string;
    details?: string | null;
    settingName?: string;
}

export interface ModuleSetting {
    // Direct boolean properties
    Enabled?: boolean | string | ModuleValue;
    enabled?: boolean | string | ModuleValue;
    active?: boolean | string;
    Active?: boolean | string;

    // Other possible properties
    Level?: string;
    levels?: string[];
    [key: string]: any; // For any other properties
}

export interface ComplianceModules {
    [moduleName: string]: ModuleSetting;
}

/**
 * Test a module setting and add it to the enabledModules set if it's enabled
 * @param module - The module name
 * @param settings - The module settings object
 * @param enabledModules - The set of enabled modules
 * @returns The updated set of enabled modules
 */
const testModule = (module: string, settings: ModuleSetting, enabledModules: Set<string> = new Set<string>()): Set<string> => {
    // Special case: the mere presence of iso27001-2013 in the document means it's enabled
    if (module === 'iso27001-2013') {
        enabledModules.add(module.toLowerCase());
        // console.log(`Module ${module} is enabled by presence`);
        return enabledModules; // Return the updated set
    }

    // Check various possible enabled flags for other modules
    if (settings && typeof settings === 'object') {
        let isEnabled = false;

        // Case 1: Direct boolean properties
        if (
            settings.Enabled === true || settings.Enabled === 'true' ||
            settings.enabled === true || settings.enabled === 'true' ||
            settings.active === true || settings.active === 'true' ||
            settings.Active === true || settings.Active === 'true'
        ) {
            isEnabled = true;
        }

        // Case 2: Nested object with value property
        else if (settings.Enabled && typeof settings.Enabled === 'object' && settings.Enabled !== null) {
            const enabledSetting = settings.Enabled as ModuleValue;
            if (
                (enabledSetting.value === true || enabledSetting.value === 'true') ||
                (settings.enabled && typeof settings.enabled === 'object' &&
                    ((settings.enabled as ModuleValue).value === true || (settings.enabled as ModuleValue).value === 'true'))
            ) {
                isEnabled = true;
            }
        }

        // console.log(`Module ${module}, Enabled: ${isEnabled}`);

        if (isEnabled) {
            enabledModules.add(module.toLowerCase());
        }
    }

    return enabledModules;
}

/**
 * Gets the set of enabled modules for the current company
 * @returns A set of enabled module names in lowercase
 */
export const getEnabledModules = async (): Promise<Set<string>> => {
    try {
        const enabledModules = new Set<string>();

        // Check if SSR flags contain compliance modules
        // if (typeof window !== 'undefined' && window._SSR_FLAGS && window._SSR_FLAGS.complianceModules) {
        //     const data = window._SSR_FLAGS.complianceModules as ComplianceModules | undefined;
        //     if (data) {
        //         Object.entries(data).forEach(([module, settings]: [string, ModuleSetting]) =>
        //             testModule(module, settings, enabledModules));
        //     }

        //     console.log('Enabled modules:', enabledModules);
        //     return enabledModules;
        // }

        const companyID = window?._SSR_FLAGS && window._SSR_FLAGS?.OrganizationID ? window._SSR_FLAGS?.OrganizationID : await waitForCompanyID();
        const companySettingsRef = db.doc(`/customers/${companyID}/companySettings/complianceModules`);
        const companySettingsDoc = await companySettingsRef.get();

        if (companySettingsDoc.exists) {
            const data = companySettingsDoc.data() as ComplianceModules | undefined;
            
            // console.log('[ControlsService] Compliance Module Data', data);
            if (data) {
                Object.entries(data).forEach(([module, settings]: [string, ModuleSetting]) =>
                    testModule(module, settings, enabledModules));
            }
        }

        // console.log('Enabled modules:', enabledModules);
        return enabledModules;
    } catch (error) {
        console.error('Error getting enabled modules:', error);
        return new Set<string>(); // Return empty set as fallback
    }
};

/**
 * Converts a standard name to its module key format (camelCase)
 * @param standardName - Standard name (e.g., "ISO 27001")
 * @returns The module key (e.g., "iso27001")
 */
export const standardNameToModuleKey = (standardName: string): string => {
    if (!standardName) return '';

    // First convert the entire string to lowercase
    const originalString = standardName.trim();
    let moduleKey = originalString.toLowerCase();

    // Handle ISO 27001 - all versions use the 'iso27001-2013' key
    if (moduleKey.match(/^iso\s*27001/i)) {
        return 'iso27001-2013';
    }

    // Reset moduleKey (in case we modified it in the specific checks above)
    moduleKey = originalString.toLowerCase();

    // Replace "iso/iec " prefix with "iso" (preserve the iso prefix)
    moduleKey = moduleKey.replace(/^iso\/iec\s+/, 'iso');

    // Replace "iso " with "iso" (no space, preserve the iso prefix)
    moduleKey = moduleKey.replace(/^iso\s+/, 'iso');

    // Remove spaces and special characters
    moduleKey = moduleKey.replace(/[\s\-\.\/]/g, '');

    return moduleKey;
};

/**
 * Checks if a standard is enabled
 * @param standardName - The name of the standard to check (e.g., "ISO 27001")
 * @returns Whether the standard is enabled
 */
export const isStandardEnabled = async (standardName: string): Promise<boolean> => {
    if (!standardName) return false;

    const enabledModules = await getEnabledModules();
    const moduleKey = standardNameToModuleKey(standardName);

    // console.log(`Checking if ${standardName} (key: ${moduleKey}) is enabled. Enabled modules:`, enabledModules);

    return enabledModules.has(moduleKey);
};